import type PartnerService from 'tio-employee/services/partner';
import type EmployeeService from 'tio-employee/services/employee';
import type SessionContextService from 'tio-employee/services/session-context';
import type { BenefitFlags } from 'tio-employee/utils/beta/benefit-rows';
import type { DemoScenario } from 'tio-employee/utils/career-pathways-demo-data';

/** The benefits the active demo scenario models; `null` outside a demo session. */
export type DemoBenefitScenario = DemoScenario['features'] | null;

export interface BenefitFlagSources {
  partner: PartnerService;
  employee: EmployeeService;
  sessionContext: SessionContextService;
  /**
   * The authenticated route's raw user-profile wellness payload, flat and snake_case; a `D-5`
   * `wellness_train` hides the wellness row.
   */
  wellnessData: Record<string, unknown> | null | undefined;
  /** The demo scenario's benefits (`demoMode.scenarioFeatures`), which decide loan repayment and wellness in a demo; `null` (the default) reads the live employee. */
  scenario?: DemoBenefitScenario;
}

/**
 * Reads the visibility flags the authenticated shell hands the side nav, so a
 * page can gate its own benefit list on exactly the same inputs.
 *
 * @param sources the partner, employee and session-context services, the wellness payload, and the demo scenario when one is active
 * @returns the flags `benefitRows` consumes
 */
export function benefitFlags({
  partner,
  employee,
  sessionContext,
  wellnessData,
  scenario = null,
}: BenefitFlagSources): BenefitFlags {
  const roles = sessionContext.rolesForCurrentCompany;
  const isTaOnly = Boolean(partner.isTaOnly || employee.isTaOnlyEmployee);
  const plan = employee.current.plan;
  const wellnessTrain = (wellnessData as { wellness_train?: string } | null)?.wellness_train;

  return {
    isTaOnly,
    isParticipant: roles.some((role) => role.relationshipType === 'PARTICIPANT') && !isTaOnly,
    isFamilyMember: Boolean(sessionContext.isFamilyMember),
    showTuitionAssistance: Boolean(partner.company?.taEnabled),
    showWellness: scenario ? scenario.financialWellness : wellnessTrain !== 'D-5',
    showPslf: Boolean(partner.isPslfEnabled) && !employee.hasRogueFein,
    showSlr: scenario ? scenario.studentLoanRepayment : Boolean(plan && plan.cadence !== 'NEVER'),
    showSyf: Boolean(partner.isSyfEnabled),
    // A repayment plan for loans the scenario does not model is a dead end.
    showIdr: Boolean(partner.isIdrEnabled) && (scenario?.studentLoanRepayment ?? true),
    isLpnEnabled: lpnEnabled(partner),
  };
}

function lpnEnabled(partner: PartnerService): boolean {
  const { lpn } = partner.companySettings;
  if (lpn && 'isEnabled' in lpn) {
    return lpn.isEnabled as boolean;
  }
  return false;
}
